
import { useState, useEffect } from "react";
import { ImportSettings as ImportSettingsType } from "@/types/import";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";

interface ImportSettingsProps {
  settings: ImportSettingsType;
  onChange: (settings: ImportSettingsType) => void;
}

export const ImportSettings = ({
  settings,
  onChange,
}: ImportSettingsProps) => {
  const [localSettings, setLocalSettings] = useState<ImportSettingsType>(settings);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSettingsChange = <K extends keyof ImportSettingsType>(
    field: K,
    value: ImportSettingsType[K]
  ) => {
    const updatedSettings = {
      ...localSettings,
      [field]: value,
    };
    setLocalSettings(updatedSettings);
    onChange(updatedSettings);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="delimiter">CSV Delimiter</Label>
          <Select
            value={localSettings.delimiter}
            onValueChange={(value) => handleSettingsChange("delimiter", value)}
          >
            <SelectTrigger id="delimiter">
              <SelectValue placeholder="Select delimiter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=",">Comma (,)</SelectItem>
              <SelectItem value=";">Semicolon (;)</SelectItem>
              <SelectItem value="\t">Tab</SelectItem>
              <SelectItem value="|">Pipe (|)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="encoding">File Encoding</Label>
          <Select
            value={localSettings.encoding}
            onValueChange={(value) => handleSettingsChange("encoding", value)}
          >
            <SelectTrigger id="encoding">
              <SelectValue placeholder="Select encoding" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="UTF-8">UTF-8</SelectItem>
              <SelectItem value="ISO-8859-1">ISO-8859-1 (Latin-1)</SelectItem>
              <SelectItem value="UTF-16">UTF-16</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <h4 className="font-medium">Processing Options</h4>
        
        <div className="flex items-center space-x-2">
          <Checkbox
            id="has-header"
            checked={localSettings.hasHeader}
            onCheckedChange={(checked) =>
              handleSettingsChange("hasHeader", checked as boolean)
            }
          />
          <Label htmlFor="has-header">File has header row</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="trim-fields"
            checked={localSettings.trimFields}
            onCheckedChange={(checked) =>
              handleSettingsChange("trimFields", checked as boolean)
            }
          />
          <Label htmlFor="trim-fields">Trim whitespace from fields</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="skip-empty-lines"
            checked={localSettings.skipEmptyLines}
            onCheckedChange={(checked) =>
              handleSettingsChange("skipEmptyLines", checked as boolean)
            }
          />
          <Label htmlFor="skip-empty-lines">Skip empty lines</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="create-missing-categories"
            checked={localSettings.createMissingCategories}
            onCheckedChange={(checked) =>
              handleSettingsChange("createMissingCategories", checked as boolean)
            }
          />
          <Label htmlFor="create-missing-categories">Create missing categories</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="skip-duplicate-quotes"
            checked={localSettings.skipDuplicateQuotes}
            onCheckedChange={(checked) =>
              handleSettingsChange("skipDuplicateQuotes", checked as boolean)
            }
          />
          <Label htmlFor="skip-duplicate-quotes">Skip duplicate quotes</Label>
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <h4 className="font-medium">Batch Processing</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="batch-size">Batch Size</Label>
            <Input
              id="batch-size"
              type="number"
              min="1"
              max="1000"
              value={localSettings.batchSize.toString()}
              onChange={(e) =>
                handleSettingsChange("batchSize", parseInt(e.target.value) || 100)
              }
            />
            <p className="text-sm text-muted-foreground">
              Number of quotes to process in each batch (1-1000)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
